"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import FadeIn from "../animations/FadeIn";
import CardCarousel from "./CardCarousel";

export interface PreferredItem {
    id: string | number;
    link: string;
    image: string;
    title: string;
}

type PreferredTreatmentsDict = {
    home?: { see_all_treatments?: string };
};

export default function PreferredTreatmentsCarousel({
    treatments,
    title,
    description,
    dict,
}: {
    treatments: PreferredItem[];
    title?: string;
    description?: string;
    dict?: PreferredTreatmentsDict;
}) {
    if (treatments.length === 0) return null;

    const seeAll = dict?.home?.see_all_treatments || "See All Treatments";
    const [item1, item2, item3, item4] = treatments;

    const overlay = (
        <div className="absolute inset-0 bg-gradient-to-t from-[#00AEEF]/75 via-[#00AEEF]/20 to-transparent opacity-90 transition-opacity"></div>
    );

    const cardBody = (item: PreferredItem, titleSize: string) => (
        <>
            <Image
                src={item.image}
                alt={item.title}
                fill
                quality={75}
                sizes="(max-width: 1024px) 100vw, 33vw"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            {overlay}
            <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className={`${titleSize} font-bold mb-2 tracking-tight leading-tight`}>{item.title}</h3>
                <span className="flex items-center text-[13px] font-medium opacity-95 group-hover:opacity-100 transition-opacity">
                    {seeAll} <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </span>
            </div>
        </>
    );

    return (
        <section className="py-16 sm:py-20 bg-white">
            <div className="container-custom">
                <FadeIn className="text-center mb-8 md:mb-12">
                    {title && <h2 className="text-3xl font-bold text-[#0f4c81] mb-4 tracking-tight">{title}</h2>}
                    {description && (
                        <p className="text-gray-600 max-w-3xl mx-auto text-[16px] leading-relaxed">
                            {description}
                        </p>
                    )}
                </FadeIn>

                {/* Desktop: mosaic grid (tall · stacked×2 · tall) */}
                <div className="hidden lg:grid grid-cols-3 gap-6 h-[550px]">
                    {item1 && (
                        <FadeIn delay={100} className="h-full">
                            <Link href={item1.link} className="group relative block w-full h-full rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                                {cardBody(item1, "text-2xl")}
                            </Link>
                        </FadeIn>
                    )}

                    <div className="flex flex-col gap-6 h-full">
                        {[item2, item3].map((item, idx) => item && (
                            <FadeIn key={item.id} delay={200 + idx * 100} className="flex-1 min-h-0">
                                <Link href={item.link} className="group relative block w-full h-full rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                                    {cardBody(item, "text-xl")}
                                </Link>
                            </FadeIn>
                        ))}
                    </div>

                    {item4 && (
                        <FadeIn delay={400} className="h-full">
                            <Link href={item4.link} className="group relative block w-full h-full rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                                {cardBody(item4, "text-2xl")}
                            </Link>
                        </FadeIn>
                    )}
                </div>

                {/* Mobile / tablet: carousel */}
                <div className="lg:hidden">
                    <CardCarousel
                        ariaLabel="treatment"
                        itemClassName="w-[80%] sm:w-[52%] md:w-[40%]"
                        items={treatments.map((item) => (
                            <Link key={item.id} href={item.link} className="group relative flex h-[360px] sm:h-[400px] rounded-[20px] overflow-hidden shadow-[0_12px_30px_rgba(5,30,60,0.12)] hover:shadow-[0_20px_45px_rgba(5,30,60,0.22)] ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1.5">
                                <Image
                                    src={item.image}
                                    alt={item.title}
                                    fill
                                    quality={75}
                                    sizes="(max-width: 640px) 80vw, 52vw"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                {overlay}
                                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                                    <h3 className="text-2xl font-bold mb-2 tracking-tight leading-tight">{item.title}</h3>
                                    <span className="flex items-center text-[13px] font-medium opacity-95 group-hover:opacity-100 transition-opacity">
                                        {seeAll} <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    />
                </div>
            </div>
        </section>
    );
}
